import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Modal, Notice } from '../patterns';
import {
  getProductLedgerRows, relabelLedgerRow, LedgerRow,
} from '../../services/consumableInventoryService';
import { formatDate } from '../../dateFormat';
import { editPathFor } from './unitReviewFix';

export interface UnitReviewFixModalProps {
  productId: string;
  productName: string;
  onClose: () => void;
  /** Called after at least one row was relabelled, so the parent can refetch. */
  onResolved: () => void;
}

/**
 * Admin resolution surface (Slice 6): relabel the unit on individual ledger
 * rows that block a product's on-hand figure. The quantity is never changed —
 * this is strictly the "unit label is wrong" correction. The unit selector is
 * limited to the product's stockUnit family (server-enforced too), so a fix
 * always clears the conflict flag — but that's only the right fix when the
 * product's own stockUnit is correct; otherwise a notice here points at the
 * product form instead, since every option in the selector would be wrong.
 */
const UnitReviewFixModal: React.FC<UnitReviewFixModalProps> = ({ productId, productName, onClose, onResolved }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<LedgerRow[]>([]);
  const [allowedUnits, setAllowedUnits] = useState<string[]>([]);
  const [stockUnit, setStockUnit] = useState('');
  const [consumableType, setConsumableType] = useState('');
  const [pick, setPick] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [didResolve, setDidResolve] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getProductLedgerRows(productId)
      .then((data) => {
        if (cancelled) return;
        setRows(data.conflictRows);
        setAllowedUnits(data.allowedUnits);
        setStockUnit(data.stockUnit);
        setConsumableType(data.consumableType);
        // Default each row's target to the product's stockUnit.
        setPick(Object.fromEntries(data.conflictRows.map((r) => [r._id, data.stockUnit])));
        setLoading(false);
      })
      .catch(() => { if (!cancelled) { setError(t('consumables.unitReview.fixModal.loadError')); setLoading(false); } });
    return () => { cancelled = true; };
  }, [productId, t]);

  const reasonLabel = (code: string | null): string =>
    code ? t(`inventory.unitConflict.reason.${code}`, { defaultValue: code }) : '';

  const handleSave = async (row: LedgerRow) => {
    setSavingId(row._id);
    setRowError((prev) => ({ ...prev, [row._id]: '' }));
    try {
      await relabelLedgerRow(productId, row._id, pick[row._id] || stockUnit);
      const remaining = rows.filter((r) => r._id !== row._id);
      setRows(remaining);
      setDidResolve(true);
      if (remaining.length === 0) {
        onResolved();
        onClose();
      }
    } catch {
      setRowError((prev) => ({ ...prev, [row._id]: t('consumables.unitReview.fixModal.saveError') }));
    } finally {
      setSavingId(null);
    }
  };

  const close = () => {
    // If we fixed some but not all, still let the parent refresh its list.
    if (didResolve) onResolved();
    onClose();
  };

  return (
    <Modal show onHide={close} title={t('consumables.unitReview.fixModal.title', { product: productName })}>
      <Modal.Body>
        {loading && <div>{t('common.loading', { defaultValue: 'Loading…' })}</div>}
        {error && <Notice tone="danger">{error}</Notice>}
        {!loading && !error && rows.length === 0 && (
          <Notice tone="success">{t('consumables.unitReview.fixModal.empty')}</Notice>
        )}
        {!loading && !error && rows.length > 0 && (
          <>
            <p>{t('consumables.unitReview.fixModal.intro', { unit: stockUnit })}</p>
            <Notice tone="info">
              {t('consumables.unitReview.fixModal.wrongProductUnit', { unit: stockUnit })}
              {' '}
              <Link to={editPathFor(consumableType, productId)}>
                {t('consumables.unitReview.fixModal.wrongProductUnitLink', { product: productName })}
              </Link>
            </Notice>
            <table className="pat-table" data-testid="fix-modal-rows">
              <thead>
                <tr>
                  <th>{t('consumables.unitReview.fixModal.col.date')}</th>
                  <th>{t('consumables.unitReview.fixModal.col.type')}</th>
                  <th>{t('consumables.unitReview.fixModal.col.qty')}</th>
                  <th>{t('consumables.unitReview.fixModal.col.current')}</th>
                  <th>{t('consumables.unitReview.fixModal.col.newUnit')}</th>
                  <th aria-label="actions" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r._id}>
                    <td>{r.date ? formatDate(r.date, { timeZone: 'UTC' }) : '—'}</td>
                    <td>{r.type}</td>
                    <td>{r.quantity}</td>
                    <td>
                      {r.quantityUnit ?? t('consumables.unitReview.fixModal.noUnit')}
                      <div style={{ fontSize: 11, color: 'var(--shell-text-muted)' }}>{reasonLabel(r.reason)}</div>
                    </td>
                    <td>
                      <select
                        className="pat-input"
                        aria-label={t('consumables.unitReview.fixModal.col.newUnit')}
                        value={pick[r._id] ?? stockUnit}
                        onChange={(e) => setPick((prev) => ({ ...prev, [r._id]: e.target.value }))}
                      >
                        {allowedUnits.map((u) => (
                          <option key={u} value={u}>{u}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="pat-btn pat-btn--primary"
                        disabled={savingId === r._id}
                        onClick={() => handleSave(r)}
                      >
                        {savingId === r._id
                          ? t('consumables.unitReview.fixModal.saving')
                          : t('consumables.unitReview.fixModal.save')}
                      </button>
                      {rowError[r._id] && (
                        <div style={{ fontSize: 11, color: 'var(--pat-danger, #b00)' }}>{rowError[r._id]}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="pat-btn pat-btn--subtle" onClick={close}>
          {t('common.close', { defaultValue: 'Close' })}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default UnitReviewFixModal;
